import type { TokenId, TransactionHash } from "@/types";
import { useDialogPortal, useTransactionFees, useSimpleGasEstimation, useTransactionExecution } from "@/hooks";
import { redeemEventEmitter, REDEEM_EVENTS } from "@/events";
import { gatewayRedeemTransaction, estimateRedeemTransactionGas } from "@/services";
import type { WalletClient } from "viem";
import { TOKEN_IDS } from "@/types";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";
import { QUERY_KEYS, getGatewayContractAddress } from "@/config";
import { mainnet } from "viem/chains";
import { useAccount, useChainId } from "wagmi";
import { useQueryClient } from "@tanstack/react-query";

interface RedeemTransactionProps {
    redeemAmount: number;
    redeemTokenId: TokenId;
}

export function useRedeemTransaction({ redeemAmount, redeemTokenId }: RedeemTransactionProps) {
    const { closeDialog } = useDialogPortal();
    const id = useChainId();
    const { address } = useAccount();

    const { isApproved, isApproving } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.IUSD,
        amount: redeemAmount,
        spender: getGatewayContractAddress(id ?? mainnet.id),
    });

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useSimpleGasEstimation({
        estimateGasFn: (walletClient: WalletClient) => {
            return estimateRedeemTransactionGas(redeemAmount, walletClient);
        },
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const { invalidateQueries } = useQueryClient();
    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeRedeemTransaction,
    } = useTransactionExecution({
        executeTransactionFn: (walletClient) => gatewayRedeemTransaction(redeemAmount, walletClient),
        onSuccess: (hash: TransactionHash) => {
            redeemEventEmitter.emit(REDEEM_EVENTS.SUCCESS, {
                transactionHash: hash,
                redeemTokenId,
                redeemAmount,
            });
            closeDialog();
            invalidateQueries({
                queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
            });
            invalidateQueries({
                queryKey: QUERY_KEYS.REDEMPTIONS.USER(address ?? ""),
            });
        },
        errorMessage: "Failed to redeem tokens",
    });

    return {
        redeemAmount,
        gasPriceUSD,
        slippagePercent,
        transactionHash,
        executeRedeemTransaction,
        isLoading: isExecuting || isApproving,
        isEstimatingGas,
        error: gasError || transactionError,
        gasEstimate,
        needsApproval: !isApproved,
        isExecuting,
    };
}
